import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const LiveTracking = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What live tracking is</Typography>
      <Typography>
        The first time a journey's crew status changes to <strong>Mobile</strong>,
        AmbuNet can text the patient and/or email the ward a link to a simple,
        public tracking page — no AmbuNet login required. The page shows a
        plain-language status ("Your transport is on the way", "Your
        transport has arrived to collect you", and so on) and, while the crew
        is Mobile, an estimated arrival time.
      </Typography>
      <Typography>
        The ETA is live where it can be: the page prefers an estimate computed
        from the vehicle's streamed position — the same consent-based location
        sharing the dispatch map uses — and only trusts a position reported in
        the last 5 minutes. If the crew aren't sharing their location, the
        one-off estimate taken when they set off is shown for up to 15
        minutes; after that, no ETA is shown at all rather than a misleading
        one. The status headline always shows either way.
      </Typography>
      <Alert severity="info" variant="outlined">
        There is no map, and no other patients' information is ever shown —
        the page is a status line and an arrival time, nothing more. The
        wording changes to a simple "journey complete" message once the
        journey finishes.
      </Alert>
      <Screenshot url="patient-transport/live-tracking.png">
        The public journey-tracking page showing the status headline and
        estimated arrival time.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Turning it on</Typography>
      <Typography>
        Two independent switches control who is texted or emailed, under{' '}
        <DocLink
          url="/site-configuration/pts-bookings"
          text="Configuration > Patient Transport > PTS Bookings"
        />
        : <strong>Text the patient</strong> and <strong>Email the ward</strong>.
        Either, both, or neither can be enabled. The patient's phone number
        comes from their patient record; the ward's email comes from the ward
        or department's contact email on the organisation record.
      </Typography>
      <Typography>
        If a phone number or ward email is missing, invalid, or the message
        simply fails to send, the journey's status update still goes through
        as normal — sending the notification never blocks or delays a crew's
        workflow. There is currently no on-screen indicator if a notification
        failed to send, so if a patient or ward says they never got a link,
        that's the most likely explanation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Link expiry</Typography>
      <Typography>
        A tracking link stops working 6 hours after it's issued, regardless of
        what happened to the journey in that time.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Sharing the link responsibly</Typography>
      <Typography>
        Because the page needs no login, anyone who has the link can open it —
        there is no check that the person opening it is the patient, a
        relative, or the ward. Treat it the same way you'd treat any other
        text message containing appointment information: it's meant for the
        patient (or the ward, for the email copy) and shouldn't be forwarded
        on casually.
      </Typography>
    </Stack>
  </Stack>
)

export default LiveTracking
